import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Mail, Phone, Send, CheckCircle, Clock, Users, Headphones } from 'lucide-react';

function SupportPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    priority: 'Medium',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const supportCategories = [
    'Technical Issue',
    'Course Content',
    'Billing Question',
    'Account Problem',
    'Feature Request',
    'General Inquiry'
  ];

  const priorityLevels = [
    { value: 'Low', label: 'Low', description: 'Non-urgent questions' },
    { value: 'Medium', label: 'Medium', description: 'Standard support request' },
    { value: 'High', label: 'High', description: 'Urgent issues affecting learning' },
    { value: 'Critical', label: 'Critical', description: 'Platform down or major issue' }
  ];

  const supportStats = [
    { icon: Clock, number: '< 2 hours', label: 'Average Response Time' },
    { icon: Users, number: '24/7', label: 'Support Availability' },
    { icon: CheckCircle, number: '98%', label: 'Issue Resolution Rate' },
    { icon: Headphones, number: '50+', label: 'Support Agents' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 to-blue-50 py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              Support Center
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              We're here to help you succeed. Get personalized support from our expert team 
              whenever you need assistance with your learning journey.
            </p>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Commitment</h2>
              <p className="text-lg text-slate-700 leading-relaxed">
                Your success is our priority. We provide comprehensive support through multiple channels 
                to ensure you have the best learning experience possible.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Support Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {supportStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-2">{stat.number}</h3>
                <p className="text-slate-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Form */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Get Support</h2>
              <p className="text-lg text-slate-600">
                Fill out the form below and our support team will get back to you as soon as possible.
              </p>
            </motion.div>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
              >
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-green-900 mb-2">Support Request Submitted!</h3>
                <p className="text-green-700 mb-4">
                  Thank you for contacting us. We've received your support request and will respond within 2 hours.
                </p>
                <div className="bg-white rounded-lg p-4 text-left max-w-md mx-auto">
                  <h4 className="font-semibold text-green-900 mb-2">What happens next?</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• You'll receive a confirmation email</li>
                    <li>• Our team will review your request</li>
                    <li>• We'll respond with a solution or follow-up questions</li>
                    <li>• We'll work with you until the issue is resolved</li>
                  </ul>
                </div>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select a category</option>
                      {supportCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Priority Level
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {priorityLevels.map((level) => (
                        <option key={level.value} value={level.value}>
                          {level.label} - {level.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Brief description of your issue"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Please provide detailed information about your issue, including any error messages, steps to reproduce the problem, and what you were trying to accomplish..."
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white py-4 rounded-lg font-semibold text-lg transition-colors duration-300 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting Request...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit Support Request
                    </>
                  )}
                </button>
              </motion.form>
            )}
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Other Ways to Reach Us</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Live Chat</h3>
                <p className="text-slate-600 mb-4">Get instant help from our support team</p>
                <p className="text-sm text-blue-600 font-semibold mb-6">Available 24/7</p>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300">
                  Start Chat
                </button>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Email Support</h3>
                <p className="text-slate-600 mb-4">Send us a detailed message</p>
                <p className="text-sm text-green-600 font-semibold mb-6">Response within 2 hours</p>
                <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300">
                  Send Email
                </button>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Phone Support</h3>
                <p className="text-slate-600 mb-4">Speak directly with our team</p>
                <p className="text-sm text-purple-600 font-semibold mb-6">Mon-Fri, 9AM-6PM EST</p>
                <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300">
                  Call Now
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Quick Answers</h2>
            <p className="text-lg text-slate-600 mb-8">
              Check out our frequently asked questions for instant answers to common issues.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-lg text-left">
                <h3 className="font-bold text-slate-900 mb-2">How do I reset my password?</h3>
                <p className="text-slate-600 text-sm">Go to the login page and click "Forgot Password" to receive reset instructions via email.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg text-left">
                <h3 className="font-bold text-slate-900 mb-2">Can I get a refund?</h3>
                <p className="text-slate-600 text-sm">Yes, we offer a 30-day money-back guarantee for all courses.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg text-left">
                <h3 className="font-bold text-slate-900 mb-2">How do I download certificates?</h3>
                <p className="text-slate-600 text-sm">Complete your course and find the certificate download link in your dashboard.</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg text-left">
                <h3 className="font-bold text-slate-900 mb-2">Is there a mobile app?</h3>
                <p className="text-slate-600 text-sm">Yes, download our mobile app from the App Store or Google Play.</p>
              </div>
            </div>
            
            <a
              href="/help"
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300"
            >
              View All FAQs
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default SupportPage;
